import {
    PlusCircleIcon,
    CloudArrowUpIcon,
    ChatBubbleLeftRightIcon,
    TableCellsIcon,
    BellAlertIcon,
    FunnelIcon,
    IdentificationIcon,
    CubeIcon,
    PlayCircleIcon,
    QuestionMarkCircleIcon,
    BookOpenIcon,
    UserGroupIcon,
    Cog6ToothIcon,
    BuildingOffice2Icon
} from '@heroicons/react/24/outline';
import OnboardingSlide from './OnboardingSlide';
import WelcomeSlide from './WelcomeSlide';
import { type Slide } from '../whatsNew/WhatsNewContent';

const PLACEHOLDER_VIDEO_URL = 'https://youtu.be/dQw4w9WgXcQ';

export const onboardingSlides: Slide[] = [
    {
        id: 'welcome',
        label: 'Welcome',
        content: <WelcomeSlide />
    },
    {
        id: 'create-case',
        label: 'Create a Case',
        content: (
            <OnboardingSlide
                title="New Patients"
                subtitle="Add a patient, upload their records, and submit the case in minutes."
                videoUrl={PLACEHOLDER_VIDEO_URL}
                features={[
                    {
                        icon: PlusCircleIcon,
                        iconBg: 'bg-blue-100',
                        iconColor: 'text-blue-600',
                        title: 'New Patient',
                        description:
                            'Create a patient profile with their details, photos, and dental records in just a few clicks.'
                    },
                    {
                        icon: CloudArrowUpIcon,
                        iconBg: 'bg-green-100',
                        iconColor: 'text-green-600',
                        title: 'Upload & Submit',
                        description:
                            'Upload STL files, radiographs, and photos, then submit the case for review.'
                    },
                    {
                        icon: ChatBubbleLeftRightIcon,
                        iconBg: 'bg-indigo-100',
                        iconColor: 'text-indigo-600',
                        title: 'Chat',
                        description:
                            'Have questions? Chat directly with the ODO team on any case at any time.'
                    }
                ]}
            />
        )
    },
    {
        id: 'patient-table',
        label: 'Patient Table',
        content: (
            <OnboardingSlide
                title="Your Patient Table"
                subtitle="All your patients in one place. Cases that need your attention always come first."
                videoUrl={PLACEHOLDER_VIDEO_URL}
                features={[
                    {
                        icon: TableCellsIcon,
                        iconBg: 'bg-teal-100',
                        iconColor: 'text-teal-600',
                        title: 'All Patients',
                        description: 'View and manage every patient from a single, organized table.'
                    },
                    {
                        icon: BellAlertIcon,
                        iconBg: 'bg-amber-100',
                        iconColor: 'text-amber-600',
                        title: 'Action Required',
                        description:
                            'Patients needing your attention are automatically surfaced to the top of the list.'
                    },
                    {
                        icon: FunnelIcon,
                        iconBg: 'bg-purple-100',
                        iconColor: 'text-purple-600',
                        title: 'Filter & Search',
                        description: 'Quickly find any patient with built-in filters and search.'
                    }
                ]}
            />
        )
    },
    {
        id: 'patient-card',
        label: 'Patient Card',
        content: (
            <OnboardingSlide
                title="The Patient Card"
                subtitle="Everything about a patient in one central view. Records, scans, files, and chat."
                videoUrl={PLACEHOLDER_VIDEO_URL}
                features={[
                    {
                        icon: IdentificationIcon,
                        iconBg: 'bg-blue-100',
                        iconColor: 'text-blue-600',
                        title: 'Patient Info',
                        description:
                            'See all patient details, treatment plans, and case status at a glance.'
                    },
                    {
                        icon: CubeIcon,
                        iconBg: 'bg-teal-100',
                        iconColor: 'text-teal-600',
                        title: 'DM Scans & Files',
                        description:
                            'Access DM scans, STL files, radiographs, photos, and all uploaded documents.'
                    },
                    {
                        icon: ChatBubbleLeftRightIcon,
                        iconBg: 'bg-green-100',
                        iconColor: 'text-green-600',
                        title: 'Chat & Updates',
                        description:
                            'View the full conversation history and latest updates for the case.'
                    }
                ]}
            />
        )
    },
    {
        id: 'resources',
        label: 'Resources',
        content: (
            <OnboardingSlide
                title="Resources"
                subtitle="Learn at your own pace with videos, FAQs, and detailed guides."
                videoUrl={PLACEHOLDER_VIDEO_URL}
                features={[
                    {
                        icon: PlayCircleIcon,
                        iconBg: 'bg-indigo-100',
                        iconColor: 'text-indigo-600',
                        title: 'Video Tutorials',
                        description:
                            'Step-by-step video guides covering every feature of the platform.'
                    },
                    {
                        icon: QuestionMarkCircleIcon,
                        iconBg: 'bg-amber-100',
                        iconColor: 'text-amber-600',
                        title: 'FAQs',
                        description:
                            'Find instant answers to the most common questions about your workflow.'
                    },
                    {
                        icon: BookOpenIcon,
                        iconBg: 'bg-green-100',
                        iconColor: 'text-green-600',
                        title: 'Documents & Guides',
                        description:
                            'Access detailed guides and documentation for in-depth reference.'
                    }
                ]}
            />
        )
    },
    {
        id: 'account',
        label: 'Account',
        content: (
            <OnboardingSlide
                title="Account & Settings"
                subtitle="Manage your team, practice details, and account preferences."
                videoUrl={PLACEHOLDER_VIDEO_URL}
                features={[
                    {
                        icon: UserGroupIcon,
                        iconBg: 'bg-purple-100',
                        iconColor: 'text-purple-600',
                        title: 'Team Management',
                        description:
                            'Invite and manage team members who can access your practice dashboard.'
                    },
                    {
                        icon: BuildingOffice2Icon,
                        iconBg: 'bg-blue-100',
                        iconColor: 'text-blue-600',
                        title: 'Practice Settings',
                        description:
                            'Update your practice information, address, and contact details.'
                    },
                    {
                        icon: Cog6ToothIcon,
                        iconBg: 'bg-teal-100',
                        iconColor: 'text-teal-600',
                        title: 'Preferences',
                        description: 'Customize your account settings and notification preferences.'
                    }
                ]}
            />
        )
    }
];
